"use client";

import { ChevronLeft } from "lucide-react";
import * as React from "react";
import { cn } from "@/lib/utils";
import { trackMarketingDemoBooked, trackMarketingFormSubmitted } from "./analytics";
import { CalEmbed } from "./cal-embed";
import { type Brand, BRANDS } from "./demo-brands";

/**
 * Two-step book-a-demo wizard card (brand type -> cal.com scheduler). Shared by
 * the homepage/section CTA (`HomeCta`) and any page that embeds the demo form.
 * Boutique studios book a different cal.com event than multi-location/franchise.
 */
export function DemoWizard({
  title = "Book a demo to see why fitness brands are switching",
}: {
  title?: string;
}) {
  const [brand, setBrand] = React.useState<Brand | null>(null);

  function chooseBrand(next: Brand) {
    setBrand(next);
    trackMarketingFormSubmitted("demo", { brandType: next.id });
  }

  return (
    <div
      className={cn(
        "mx-auto w-full bg-background transition-[max-width] duration-300 ease-out sm:overflow-hidden sm:rounded-3xl sm:border sm:border-border sm:shadow-xl",
        // Widen on desktop once a brand is picked so the cal.com scheduler has
        // room to render in its two-column (desktop) layout.
        brand ? "max-w-2xl lg:max-w-4xl" : "max-w-2xl",
      )}
    >
      <div className="p-0 sm:p-10">
        <div className="text-center">
          <h2 className="text-2xl font-extrabold tracking-tight text-foreground text-balance sm:text-3xl md:text-[2rem] md:leading-[1.15]">
            {title}
          </h2>
          <p className="mx-auto mt-3 max-w-md leading-relaxed text-muted-foreground">
            {brand
              ? "Great. Pick a time that works for you."
              : "First, tell us about yourself. What describes your brand?"}
          </p>
        </div>

        {brand ? (
          <div className="mt-7">
            <button
              type="button"
              onClick={() => setBrand(null)}
              className="mb-4 inline-flex items-center gap-1 text-sm font-medium text-muted-foreground transition-colors hover:text-foreground"
            >
              <ChevronLeft className="size-4" />
              {brand.label}
            </button>
            <div className="overflow-hidden">
              <CalEmbed
                calLink={brand.calLink}
                onBookingSuccess={() => trackMarketingDemoBooked(brand.id)}
              />
            </div>
          </div>
        ) : (
          <div className="mt-7 grid gap-3 sm:grid-cols-3">
            {BRANDS.map((b) => (
              <button
                key={b.id}
                type="button"
                onClick={() => chooseBrand(b)}
                className="group flex flex-col items-center gap-3 rounded-2xl border border-border bg-background p-5 text-center transition-all hover:-translate-y-0.5 hover:border-primary/40 hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/50 dark:hover:border-brand-accent/40 dark:focus-visible:ring-brand-accent/50"
              >
                <span className="flex size-12 items-center justify-center rounded-xl border border-border bg-muted/50 text-muted-foreground transition-colors group-hover:border-primary/30 group-hover:bg-primary/10 group-hover:text-primary dark:group-hover:border-brand-accent/30 dark:group-hover:bg-brand-accent/10 dark:group-hover:text-brand-accent">
                  <b.icon className="size-5" />
                </span>
                <span>
                  <span className="block text-sm font-semibold text-foreground">{b.label}</span>
                  <span className="mt-0.5 block text-xs leading-snug text-muted-foreground">
                    {b.blurb}
                  </span>
                </span>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
